import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ContactActionsDrawer from "@/components/campaignTester/ContactActionsDrawer";
import DonationPanel from "@/components/campaignTester/DonationPanel";
import PostpaidPanel from "@/components/campaignTester/PostpaidPanel";
import SeedCampaignDialog from "@/components/campaignTester/SeedCampaignDialog";
import TopupPanel from "@/components/campaignTester/TopupPanel";
import { useRole } from "@/context/RoleContext";
import {
  useExpireTestCampaign,
  useExpireTestCommitments,
  usePurgeTestCampaign,
  useRenameTestCampaign,
  useTestCampaign,
  useTestCampaigns,
  useTestPostpaidStatus,
} from "@/hooks/useCampaignTester";
import type { CampaignCloseResult, CommitmentSweepResult } from "@/interfaces/campaignTester";
import { apiErrorMessage } from "@/utils/apiError-helper";

/**
 * Campaign Tester. SCAFFOLDING — deleted once Emmanuel's redemption flow calls the credit services
 * directly (jr/design/ph2/SCHEMA_DECISIONS.md §5a).
 *
 * It exists to drive credit_consumption through a realistic event sequence and make the accounting
 * visible while doing it: a commitment that posts nothing, a redemption that moves deferred revenue
 * into pending fulfilment, and a dispatch that finally recognises revenue.
 */

const STEP_COLOURS: Record<string, { bg: string; fg: string }> = {
  ready: { bg: "var(--color-wax)", fg: "var(--color-graphite)" },
  sent: { bg: "var(--color-good-night-bg)", fg: "var(--color-good-night)" },
  redeemed: { bg: "var(--color-good-sage-4)", fg: "var(--color-good-sage)" },
  shipped: { bg: "var(--color-good-ochre-4)", fg: "var(--color-good-ochre)" },
  delivered: { bg: "var(--color-good-sage-5)", fg: "var(--color-good-sage)" },
  bounced: { bg: "var(--color-good-wine-4)", fg: "var(--color-good-wine)" },
};

function StepChip({ step }: { step: string | null }) {
  const c = STEP_COLOURS[step ?? ""] ?? { bg: "var(--color-wax)", fg: "var(--color-graphite)" };
  return (
    <Chip
      size="small"
      label={step ?? "—"}
      sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 10.5 }}
    />
  );
}

function Stat({
  label,
  value,
  hint,
  colour,
}: {
  label: string;
  value: string;
  hint?: string;
  colour?: string;
}) {
  return (
    <Box sx={{ flex: 1, p: 1.6, bgcolor: "#fff" }}>
      <Typography
        sx={{
          fontSize: 9.5,
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "var(--color-graphite)",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 21, color: colour, mt: 0.6 }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>{hint}</Typography>
      )}
    </Box>
  );
}

export default function CampaignTester() {
  const { role: _role } = useRole();
  const { data: campaigns = [], isLoading, error } = useTestCampaigns();
  // Which campaign and which contact are open lives in the URL, not in state.
  //
  // Every action posts entries, and reading them means campaign → contact → entries. Held in
  // component state, any reload — an --hot rebuild included — drops you back at the campaign list
  // and you walk the whole path again. In the query string, a reload lands exactly where you were,
  // and the drawer you had open is a link you can paste to someone.
  const [params, setParams] = useSearchParams();
  const selected = params.get("campaign") ? Number(params.get("campaign")) : null;
  const openContactId = params.get("contact") ? Number(params.get("contact")) : null;
  const [seedOpen, setSeedOpen] = useState(false);

  function openCampaign(campaignId: number | null) {
    const next = new URLSearchParams(params);
    if (campaignId == null) {
      next.delete("campaign");
      next.delete("contact");
    } else {
      next.set("campaign", String(campaignId));
      next.delete("contact");
    }
    // replace: the tester is one screen with a selection, not a trail of history entries to back
    // through one at a time.
    setParams(next, { replace: true });
  }

  function openContact(contactId: number | null) {
    const next = new URLSearchParams(params);
    if (contactId == null) next.delete("contact");
    else next.set("contact", String(contactId));
    setParams(next, { replace: true });
  }
  const [simulatedDate, setSimulatedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [editing, setEditing] = useState<number | null>(null);
  const [closeResult, setCloseResult] = useState<CampaignCloseResult | null>(null);
  const [sweepResult, setSweepResult] = useState<CommitmentSweepResult[] | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);

  const { data: detail } = useTestCampaign(selected);
  // Null unless the campaign is postpaid — then the strip reads in two halves.
  const { data: postpaid } = useTestPostpaidStatus(selected);
  const purge = usePurgeTestCampaign();
  const expire = useExpireTestCampaign();
  const rename = useRenameTestCampaign();
  const sweep = useExpireTestCommitments();

  /** Save only a real change — a blur with the same text should not write, or churn the list. */
  function commitRename(campaignId: number, value: string, current: string) {
    setEditing(null);
    const title = value.trim();
    if (!title || title === current) return;
    rename.mutate({ campaignId, title });
  }
  const rows = detail?.detail ?? [];
  const state = detail?.state;
  const selectedCampaign = campaigns.find((c) => c.campaignId === selected);

  // The drawer reads its contact from the CURRENT rows, so it re-renders with the refetched data
  // after every action — no copy to keep in step, and a reload rebuilds it from the URL.
  const liveContact = useMemo(
    () =>
      openContactId == null ? null : (rows.find((r) => r.contactId === openContactId) ?? null),
    [openContactId, rows],
  );

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          The Campaign Tester is not enabled. Set <code>ENABLE_CAMPAIGN_TESTER=true</code> on the
          API — it is off by default because these actions write real credit lots and post real
          journal entries.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>
            Campaign Tester
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
            Seeded campaigns for exercising credit, commitment and accounting events.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {selected != null && (
            <Button variant="outlined" onClick={() => openCampaign(null)}>
              ← All campaigns
            </Button>
          )}
          {selected != null && (
            <Button
              variant="outlined"
              color="warning"
              disabled={sweep.isPending}
              onClick={() => {
                setCloseResult(null);
                setCloseError(null);
                setSweepResult(null);
                sweep.mutate(
                  { campaignId: selected, occurredAt: `${simulatedDate}T00:00:00.000Z` },
                  {
                    onSuccess: setSweepResult,
                    onError: (e: unknown) =>
                      setCloseError(apiErrorMessage(e, "The sweep was refused.")),
                  },
                );
              }}
            >
              Force expire commitments
            </Button>
          )}
          {selected != null && (
            <Button
              variant="outlined"
              color="warning"
              disabled={expire.isPending}
              onClick={() => {
                setCloseResult(null);
                setCloseError(null);
                expire.mutate(
                  // The simulated clock: the campaign's expire_date is set to this, then the real
                  // close runs and re-checks it for itself.
                  { campaignId: selected, occurredAt: `${simulatedDate}T00:00:00.000Z` },
                  {
                    onSuccess: setCloseResult,
                    onError: (e: unknown) =>
                      setCloseError(apiErrorMessage(e, "The close was refused.")),
                  },
                );
              }}
            >
              Expire campaign
            </Button>
          )}
          <Button variant="contained" onClick={() => setSeedOpen(true)}>
            + Seed campaign
          </Button>
        </Stack>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
        <strong>Test surface — writes real rows.</strong> Every action draws real credit lots and
        posts real journal entries against the seeded campaign. GFG-only, and never mounted in
        production. Purge deletes the campaign and everything under it.
      </Alert>

      {closeResult && (
        <Alert
          severity="success"
          sx={{ mb: 2, fontSize: 12.5 }}
          onClose={() => setCloseResult(null)}
        >
          <strong>Campaign closed{closeResult.closedNow ? "" : " (already was)"}.</strong>{" "}
          {closeResult.donated
            ? `Unspent credits donated to the campaign's causes.`
            : closeResult.skipped === "rollover"
              ? `Roll-over: ${closeResult.headroomRetired} credits of headroom retired. The credits themselves never left the lots — they stay spendable by this group's other campaigns until their own lot expiry.`
              : `Nothing unspent — the close was bookkeeping only.`}
        </Alert>
      )}
      {sweepResult && (
        <Alert
          severity={sweepResult.length === 0 ? "info" : "success"}
          sx={{ mb: 2, fontSize: 12.5 }}
          onClose={() => setSweepResult(null)}
        >
          {sweepResult.length === 0 ? (
            <>No live commitments to expire — every gift here has already been redeemed or swept.</>
          ) : (
            sweepResult.map((r) => (
              <Box key={r.campaignId}>
                <strong>
                  {r.commitments} commitment{r.commitments === 1 ? "" : "s"} expired ({r.credits}{" "}
                  credits).
                </strong>{" "}
                {r.rule === "donate"
                  ? `Donated across ${r.causeLines} cause${r.causeLines === 1 ? "" : "s"} — the credits had already left the lots at commitment, so nothing was drawn again.`
                  : `Returned to ${r.returnedToLots} lot${r.returnedToLots === 1 ? "" : "s"} and spendable again until those lots expire. Nothing posted — the client still holds the value.`}
              </Box>
            ))
          )}
        </Alert>
      )}
      {closeError && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }} onClose={() => setCloseError(null)}>
          {closeError}
        </Alert>
      )}

      {selected == null ? (
        <Box
          sx={{ border: "1px solid var(--color-card-border)", borderRadius: 2, bgcolor: "#fff" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campaign</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>On expiry</TableCell>
                <TableCell align="right">Budgeted</TableCell>
                <TableCell align="right">Consumed</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">Contacts</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && campaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: "var(--color-graphite)" }}>
                    Nothing seeded yet.
                  </TableCell>
                </TableRow>
              )}
              {campaigns.map((c) => (
                <TableRow
                  key={c.campaignId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => openCampaign(c.campaignId)}
                >
                  <TableCell sx={{ fontWeight: 700 }}>
                    {editing === c.campaignId ? (
                      // stopPropagation throughout: the row is a link to the campaign, and typing
                      // in the title must not navigate away mid-edit.
                      <TextField
                        autoFocus
                        size="small"
                        defaultValue={c.title}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => commitRename(c.campaignId, e.target.value, c.title)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: 13.5, fontWeight: 700, py: 0.5 },
                        }}
                      />
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{c.title}</span>
                        <IconButton
                          size="small"
                          aria-label="Rename campaign"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(c.campaignId);
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>{c.groupName ?? c.groupId}</TableCell>
                  <TableCell>
                    {/* Three states, not two. A ternary on a boolean reads `undefined` as false and
                        would claim "Roll over" for a response that simply predates the field —
                        asserting where the money goes on the strength of a missing key. */}
                    {c.donateUnredeemed == null ? (
                      <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                        —
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={c.donateUnredeemed ? "Donate" : "Roll over"}
                        color={c.donateUnredeemed ? "primary" : "default"}
                        sx={{ fontSize: 10.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{c.budgeted}</TableCell>
                  <TableCell align="right">{c.consumed}</TableCell>
                  <TableCell align="right">{c.remaining}</TableCell>
                  <TableCell align="right">{c.contactCount}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="inherit"
                      onClick={(e) => {
                        e.stopPropagation();
                        purge.mutate(c.campaignId);
                      }}
                    >
                      Purge
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <>
          {state && postpaid && (
            // Postpaid spends from two sources in a fixed order: the prepaid deposit (credits in
            // the lots) first, then the postpaid ceiling (invoiced after close). The prepaid-only
            // strip below reads the deposit as the whole budget, so it cannot describe this.
            <Stack
              direction="row"
              sx={{
                gap: "1px",
                bgcolor: "var(--color-card-border)",
                border: "1px solid var(--color-card-border)",
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <Stat
                label="Budget"
                value={Number(postpaid.approvedBudget).toLocaleString()}
                hint={`${Number(postpaid.deposit).toLocaleString()} prepaid + ${Number(postpaid.ceiling?.original ?? 0).toLocaleString()} ${FUNDING_MODEL_NAME.postpaid}`}
              />
              <Stat
                label="Prepaid remaining"
                value={Number(postpaid.depositRemaining).toLocaleString()}
                hint={`of the ${Number(postpaid.deposit).toLocaleString()} deposit — spent first`}
                colour="var(--color-good-night)"
              />
              <Stat
                label={`${FUNDING_MODEL_NAME.postpaid} remaining`}
                value={Number(postpaid.ceiling?.remaining ?? 0).toLocaleString()}
                hint={
                  postpaid.campaignClosed
                    ? "unused ceiling released at close"
                    : `of the ${Number(postpaid.ceiling?.original ?? 0).toLocaleString()} ceiling — invoiced after close`
                }
                colour="var(--color-good-ochre)"
              />
              <Stat
                label="Consumed"
                value={Number(state.consumed).toLocaleString()}
                hint={`${(Number(postpaid.deposit) - Number(postpaid.depositRemaining)).toLocaleString()} prepaid + ${Number(postpaid.draws.credits).toLocaleString()} ${FUNDING_MODEL_NAME.postpaid}`}
                colour="var(--color-good-sage)"
              />
              <Stat
                label="Donated"
                value={state.donated}
                hint="to causes at close"
                colour="var(--color-good-night)"
              />
            </Stack>
          )}
          {state && !postpaid && (
            <Stack
              direction="row"
              sx={{
                gap: "1px",
                bgcolor: "var(--color-card-border)",
                border: "1px solid var(--color-card-border)",
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <Stat label="Budgeted" value={state.budgeted} hint="ceiling — holds nothing" />
              <Stat
                label="Committed"
                value={state.committed}
                hint="drawn out of the lots"
                colour="var(--color-good-night)"
              />
              <Stat
                label="Consumed"
                value={state.consumed}
                // Donations are consumption events, so they are already inside this number.
                hint="redeemed + donated"
                colour="var(--color-good-sage)"
              />
              <Stat
                label="Donated"
                value={state.donated}
                hint="to causes at close"
                colour="var(--color-good-night)"
              />
              <Stat label="Remaining" value={state.remaining} hint="headroom still to draw" />
              <Stat label="Rolled over" value={state.rolledOver} hint="given back to the wallet" />
              <Stat
                label="Held"
                value={state.held}
                hint="drawn and still inside"
                colour="var(--color-good-night)"
              />
              <Stat
                label="Bank"
                value={state.bank}
                // Picks below the commitment leave a saving here; picks above spend it. Settles
                // when the last link resolves.
                hint="held − committed"
                colour={
                  Number(state.bank) < 0 ? "var(--color-good-wine)" : "var(--color-good-sage)"
                }
              />
            </Stack>
          )}

          {detail?.donation && selected != null && (
            <DonationPanel donation={detail.donation} campaignId={selected} />
          )}

          {/* NOT gated on a donation — an automated campaign's margin matters while it is live,
              which is the whole window a donation has not happened in. The panel renders nothing
              unless the campaign is maintenance-margin funded. */}
          {selected != null && <TopupPanel campaignId={selected} />}
          {/* Renders nothing unless the campaign is postpaid. */}
          {selected != null && (
            <PostpaidPanel campaignId={selected} simulatedDate={simulatedDate} />
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              bgcolor: "var(--color-good-night-bg)",
              border: "1px solid var(--color-good-night-4)",
              borderRadius: 2,
              p: 1.4,
              mb: 2,
            }}
          >
            <TextField
              type="date"
              size="small"
              label="Simulated date"
              value={simulatedDate}
              onChange={(e) => setSimulatedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Typography sx={{ fontSize: 11.5, color: "var(--color-good-night)" }}>
              Every action is stamped with this date. Expiry windows run 30–90 days or longer, so
              the donation path cannot be exercised in real time. Back-dating past a <em>closed</em>{" "}
              accounting period still rolls the journal entry forward — the clock cannot post into
              one.
            </Typography>
          </Stack>

          <Box
            sx={{ border: "1px solid var(--color-card-border)", borderRadius: 2, bgcolor: "#fff" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Committed</TableCell>
                  <TableCell>Send date</TableCell>
                  <TableCell>Commit expiry</TableCell>
                  <TableCell>Lots</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.contactId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openContact(r.contactId)}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{r.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>
                        {r.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StepChip step={r.step} />
                    </TableCell>
                    <TableCell align="right">{r.committed ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>{r.sendDate ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 11.5 }}>
                      {r.commitmentExpiry?.slice(0, 10) ?? "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                      {r.lots.length === 0 && !r.ceilingDrawn
                        ? "—"
                        : r.lots.map((l) => `#${l.lotId}:${l.amount}`).join("  ")}
                      {/* Postpaid: a gift past the deposit is drawn from the billable ceiling,
                          not from a lot — invoiced after close. */}
                      {r.ceilingDrawn && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={`ceiling ${r.ceilingDrawn}`}
                          sx={{ fontSize: 10.5, ml: r.lots.length > 0 ? 1 : 0 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      <SeedCampaignDialog open={seedOpen} onClose={() => setSeedOpen(false)} />
      <ContactActionsDrawer
        contact={liveContact}
        campaignId={selected ?? 0}
        manualSend={selectedCampaign?.manualSend ?? false}
        simulatedDate={simulatedDate}
        onClose={() => openContact(null)}
      />
    </Box>
  );
}
